import React, { useState, useContext, useEffect } from 'react';
import * as PropTypes from 'prop-types';
import {
  Select, Row, Input, Icon, Button, Popover, Form, Col, Typography, Divider, Alert, Spin,
} from 'antd';
import ReactCountryFlag from 'react-country-flag';
import { injectIntl, FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import {
  requestUpdateSelf,
} from '../../../state/ui/configuration/users/userUiActions';
import { requestChangePassword } from '../../../state/ui/configuration/users/passwordUiActions';
import { requestSendChangeEmailEmail, requestEmailSentTo, requestSendVerificationEmail } from '../../../state/ui/configuration/users/emailUiActions';
import { selectSessionUser } from '../../../state/ui/application/sessionUiReducer';
import './MyDetails.css';
import ViaValidationAlert from '../../../components/fields/ViaValidationAlert';
import { selectUpdateSelfRequest } from '../../../state/ui/configuration/users/userUiReducer';
import { selectChangePasswordRequest } from '../../../state/ui/configuration/users/passwordUiReducer';
import { ViaLocaleContext } from '../../../contexts/ViaLanguageProvider';
import { selectSendChangeEmailRequest, selectEmailSentToRequest } from '../../../state/ui/configuration/users/emailUiReducer';
import { roleNames } from '../configure/company/user-util';
import { requestSupportedLanguages } from '../../../state/ui/application/sessionUiActions';
import { selectAppConfig } from '../../../state/domain/appConfig/appConfigReducer';
import {userIsAdminConsole} from "../../../utility/security/security-util";

const { Text, Title } = Typography;


const EditEmailPopover = Form.create({ name: 'change_email' })(injectIntl(({
  form, currentAddress, onSend, setVisible, ...rest
}) => {
  const onSubmit = (e) => {
    e.preventDefault();
    form.validateFields((err, values) => {
      if (!err) {
        onSend(values.username);
        setVisible(false);
      }
    });
  };

  const intl = useIntl();
  return (
    <Popover
      {...rest}
      trigger="click"
      placement="right"
      style={{
        width: '300px',
      }}
      title={intl.formatMessage({ id: 'enterNewEmail' })}
      content={(
        <Form onSubmit={e => onSubmit(e)}>
          <Row span={24} style={{ width: '250px' }}>
            <Text><FormattedMessage id="activateInstruction" /></Text>
          </Row>
          <Row span={24}>
            <Form.Item
              className="my-details-form-item"
              style={{ marginBottom: '0', marginRight: '0.0em' }}
            >
              {form.getFieldDecorator('username', {
                rules: [
                  { required: true, message: intl.formatMessage({ id: 'validationEmail' }) },
                  { type: 'email', message: intl.formatMessage({ id: 'invalidEmail' }) },
                ],
              })(
                <Input
                  className="my-details-form-item"
                  prefix={<Icon type="user" style={{ color: 'rgba(0,0,0,0.25)' }} />}
                  type="email"
                  placeholder={intl.formatMessage({ id: 'email' })}
                  maxLength={80}
                  onPressEnter={onSubmit}
                />,
              )}
            </Form.Item>
          </Row>
          <Row span={24} type="flex" justify="end">
            <Form.Item style={{ marginBottom: '0' }}>
              <Button style={{ marginRight: '0.6em' }} onClick={() => { setVisible(false); }}>{intl.formatMessage({ id: 'cancel' })}</Button>
              <Button type="primary" htmlType="submit">{intl.formatMessage({ id: 'sendEmail' })}</Button>
            </Form.Item>
          </Row>
        </Form>
      )}
    />
  );
}));

EditEmailPopover.propTypes = {
  currentAddress: PropTypes.string.isRequired,
  onSend: PropTypes.func.isRequired,
};


const EmailField = ({ username, onEdit }) => {
  const [isEditVisible, setEditVisible] = useState(false);

  return (
    <Row type="flex" align="bottom">
      <Col span={17}>
        <FormattedMessage id="email" />
        :
        <Input
          className="my-details-form-item"
          prefix={<Icon type="user" style={{ color: 'rgba(0,0,0,0.25)' }} />}
          type="email"
          disabled
          placeholder={username}
          maxLength={80}
        />
      </Col>
      <Col span={4}>
        <EditEmailPopover
          visible={isEditVisible}
          onVisibleChange={setEditVisible}
          onSend={onEdit}
          currentAddress={username}
          setVisible={setEditVisible}
        >
          <Button type="primary" style={{ marginLeft: '0.6em' }} icon="edit">
            <FormattedMessage id="edit" />
          </Button>
        </EditEmailPopover>
      </Col>
    </Row>
  );
};

EmailField.propTypes = {
  username: PropTypes.string.isRequired,
  onEdit: PropTypes.func.isRequired,
};

const DisplayNamePopover = injectIntl(({
  currentDisplayName, onSend, setVisible, ...rest
}) => {
  const [newDisplayName, setNewDisplayName] = useState('');

  const intl = useIntl();
  const [validationStatus, setValidationStatus] = useState({ validateStatus: null, help: '', hasFeedback: false });

  const validate = (displayName) => {
    if (!displayName) {
      setValidationStatus({ validateStatus: 'error', help: intl.formatMessage({ id: 'validationEmpty' }) });
      return false;
    }

    if (displayName === currentDisplayName) {
      setValidationStatus({ validateStatus: 'error', help: intl.formatMessage({ id: 'Duplicate' }) });
      return false;
    }
    setValidationStatus({ validateStatus: null, help: '', hasFeedback: false });
    return true;
  };

  const onSubmit = () => {
    if (validate(newDisplayName)) {
      onSend(newDisplayName);
      setVisible(false);
      setNewDisplayName('');
    }
  };

  return (
    <Popover
      {...rest}
      trigger="click"
      placement="right"
      title={intl.formatMessage({ id: 'enterNewDisplayName' })}
      content={(
        <div>
          <Row span={24} style={{ width: '250px' }}>
            <Form.Item
              className="my-details-form-item"
              hasFeedback={validationStatus.hasFeedback}
              validateStatus={validationStatus.validateStatus}
              help={validationStatus.help}
              style={{ marginBottom: '0', marginRight: '0.0em' }}
            >
              <Input
                ref={input => input && input.focus()} // Focus the input when rendered.
                type="text"
                value={newDisplayName}
                onChange={(e) => {
                  validate(e.target.value);
                  setNewDisplayName(e.target.value);
                }}
                onPressEnter={onSubmit}
                placeholder={intl.formatMessage({ id: 'displayName' })}
              />
            </Form.Item>
          </Row>
          <Row span={24} type="flex" justify="end">
            <Form.Item style={{ marginBottom: '0' }}>
              <Button style={{ marginBottom: '0', marginRight: '0.6em' }} onClick={() => { setNewDisplayName(''); setVisible(false); }}>{intl.formatMessage({ id: 'cancel' })}</Button>
              <Button type="primary" onClick={onSubmit}>{intl.formatMessage({ id: 'save' })}</Button>
            </Form.Item>
          </Row>
        </div>
      )}
    />
  );
});

DisplayNamePopover.propTypes = {
  currentDisplayName: PropTypes.string.isRequired,
  onSend: PropTypes.func.isRequired,
};

const DisplayNameField = ({ displayName, onEdit }) => {
  const [isEditVisible, setEditVisible] = useState(false);

  return (
    <Row type="flex" align="bottom">
      <Col span={17}>
        <FormattedMessage id="displayName" />
        :
        <Input
          className="my-details-form-item"
          prefix={<Icon type="idcard" style={{ color: 'rgba(0,0,0,0.25)' }} />}
          type="username"
          disabled
          placeholder={displayName}
          maxLength={80}
        />
      </Col>
      <Col span={4}>
        <DisplayNamePopover
          visible={isEditVisible}
          onVisibleChange={setEditVisible}
          onSend={onEdit}
          currentDisplayName={displayName}
          setVisible={setEditVisible}
        >
          <Button type="primary" style={{ marginLeft: '0.6em' }} icon="edit">
            <FormattedMessage id="edit" />
          </Button>
        </DisplayNamePopover>
      </Col>
    </Row>
  );
};

DisplayNameField.propTypes = {
  displayName: PropTypes.string.isRequired,
  onEdit: PropTypes.func.isRequired,
};

const PasswordPopover = injectIntl(({
  onSend, setVisible, ...rest
}) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [textVisible, setTextVisible] = useState(false);
  const suffixIcon = textVisible ? (
    <Icon type="eye-invisible" onClick={() => setTextVisible(false)} style={{ color: 'rgba(0,0,0,.5)' }} />
  ) : (<Icon type="eye" onClick={() => setTextVisible(true)} style={{ color: 'rgba(0,0,0,.5)' }} />);
  const fieldType = textVisible ? 'text' : 'password';

  const intl = useIntl();
  const [validationStatus, setValidationStatus] = useState({ validateStatus: null, help: '', hasFeedback: false });

  const validate = (password) => {
    if (!password) {
      setValidationStatus({ validateStatus: 'error', help: intl.formatMessage({ id: 'validationEmpty' }) });
      return false;
    }

    if (password === currentPassword) {
      setValidationStatus({ validateStatus: 'error', help: intl.formatMessage({ id: 'Duplicate' }) });
      return false;
    }

    setValidationStatus({ validateStatus: null, help: '', hasFeedback: false });
    return true;
  };

  const onSubmit = () => {
    if (validate(newPassword)) {
      onSend(currentPassword, newPassword);
      setVisible(false);
      setNewPassword('');
    }
  };

  return (
    <Popover
      {...rest}
      trigger="click"
      placement="right"
      onVisibleChange={(visible) => { setVisible(visible); setCurrentPassword(''); setNewPassword(''); setValidationStatus({ validateStatus: null, help: '', hasFeedback: false }); }}
      title={intl.formatMessage({ id: 'changePwd' })}
      content={(
        <div style={{ width: '250px' }}>
          <Row span={24} style={{ marginBottom: '0.6em' }}>
            <Form.Item
              className="my-details-form-item"
              style={{ marginBottom: '0', marginRight: '0.0em' }}
            >
              <Input // Focus the input when rendered.
                type="password"
                value={currentPassword}
                onChange={(e) => {
                  setCurrentPassword(e.target.value);
                }}
                onPressEnter={onSubmit}
                placeholder={intl.formatMessage({ id: 'currentPassword' })}
              />
            </Form.Item>
          </Row>
          <Row span={24} style={{ marginBottom: '0.6em' }}>
            <Form.Item
              className="my-details-form-item"
              hasFeedback={validationStatus.hasFeedback}
              validateStatus={validationStatus.validateStatus}
              help={validationStatus.help}
              style={{ marginBottom: '0', marginRight: '0em' }}
            >
              <Input
                type={fieldType}
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  validate(e.target.value);
                }}
                onPressEnter={onSubmit}
                placeholder={intl.formatMessage({ id: 'newPassword' })}
                suffix={suffixIcon}
              />
            </Form.Item>
          </Row>
          <Row span={24} type="flex" justify="end">
            <Form.Item style={{ marginBottom: '0', justifySelf: 'right' }}>
              <Button style={{ marginBottom: '0', marginRight: '0.6em' }} onClick={() => { setCurrentPassword(''); setNewPassword(''); setVisible(false); }}>{intl.formatMessage({ id: 'cancel' })}</Button>
              <Button type="primary" onClick={onSubmit}>{intl.formatMessage({ id: 'save' })}</Button>
            </Form.Item>
          </Row>
        </div>
      )}
    />
  );
});

PasswordPopover.propTypes = {
  onSend: PropTypes.func.isRequired,
};

const PasswordField = ({ onEdit }) => {
  const [isEditVisible, setEditVisible] = useState(false);
  return (
    <Row type="flex" align="bottom">
      <Col span={17}>
        <FormattedMessage id="password" />
        :
        <Input
          className="my-details-form-item"
          prefix={<Icon type="lock" style={{ color: 'rgba(0,0,0,0.25)' }} />}
          type="password"
          disabled
          defaultValue="Congratulations!"
          maxLength={80}
        />
      </Col>
      <Col span={4}>
        <PasswordPopover
          visible={isEditVisible}
          onVisibleChange={setEditVisible}
          onSend={onEdit}
          setVisible={setEditVisible}
        >
          <Button type="primary" style={{ marginLeft: '0.6em' }} icon="edit">
            <FormattedMessage id="edit" />
          </Button>
        </PasswordPopover>
      </Col>
    </Row>
  );
};

PasswordField.propTypes = {
  onEdit: PropTypes.func.isRequired,
};

const LocaleField = ({ onEdit, languages }) => {
  const { appLocale, toggleLocale } = useContext(ViaLocaleContext);
  const onChange = (langCode) => {
    // This toggle isn't needed, it just makes the switch feel smoother because it is instant
    toggleLocale(langCode);
    onEdit(langCode);
  };

  return (
    <Row type="flex">
      <Col span={17}>
        <Select
          size="default"
          value={appLocale.code}
          className="my-details-locale"
          onChange={onChange}
        >
          { languages ? languages.map(language => (
            <Select.Option key={language.languageCode} value={language.languageCode}>
              <Row type="flex" align="middle">
                <ReactCountryFlag code={language.languageCode.slice(language.languageCode.indexOf('_') + 1, language.languageCode.length).toLowerCase()} svg />
                <Typography.Text>
                  &nbsp;
                  {language.languageDescription}
                </Typography.Text>
              </Row>
            </Select.Option>
          )) : <Select.Option key="spin" value="spin"><Spin /></Select.Option>
          }
        </Select>
      </Col>
    </Row>
  );
};

LocaleField.propTypes = {
  onEdit: PropTypes.func.isRequired,
  languages: PropTypes.arrayOf(PropTypes.shape({
    languageCode: PropTypes.string.isRequired,
    languageDescription: PropTypes.string.isRequired,
  })),
};

LocaleField.defaultProps = {
  languages: null,
};

function MyDetails({
  sessionUser, updateUser, changePassword,
  updateSelfError, updatePasswordError, changeEmail,
  changeEmailError, emailSentTo, fetchNewEmail, resendVerificationEmail,
  getAvailiableLanguages, languages,
}) {
  useEffect(() => {
    fetchNewEmail();
    if (!languages) getAvailiableLanguages();
  }, [fetchNewEmail, getAvailiableLanguages, languages]);

  const intl = useIntl();


  const [resentValidationEmail, setResentValidationEmail] = useState(false);
  const emails = 'unfinished';
  const editEmail = (newEmail) => { changeEmail(newEmail); setResentValidationEmail(false); };
  const editDisplayName = (newDisplayName) => {
    updateUser({ ...sessionUser, displayName: newDisplayName });
  };
  const editLocale = (newLocale) => { updateUser({ ...sessionUser, locale: newLocale }); };
  const editPassword = (currentPassword, newPassword) => {
    changePassword(currentPassword, newPassword, sessionUser.linkedRoles.find(roleName => roleName === roleNames.INSPECTOR));
  };

  const ResendEmailComponent = (emailAddress, onResend) => {
    if (!emailAddress) { return <></>; }
    return (
      <Alert
        style={{ marginTop: '0.6em' }}
        message={(
          <div>
            <Row>
              <FormattedMessage id="verificationPending" values={{ email: emailAddress }} />
            </Row>
            <Row>
              <Button type="primary" onClick={onResend} style={{ marginTop: '0.6em' }}><FormattedMessage id="verifyEmailResend" /></Button>
            </Row>
          </div>
        )}
        type="warning"
        showIcon
        closable
      />
    );
  };

  // MAIN COMPONENT
  return (
    <div className="my-details-layout">
      <Row type="flex" align="middle" justify="center">
        <div className="my-details-content">
          <Row align="top" justify="center" type="flex" style={{ marginBottom: '1.5em' }}>
            <Title level={2}><FormattedMessage id={userIsAdminConsole(sessionUser) ? 'accountSettings' : 'myDetails'} /></Title>
          </Row>
          <ViaValidationAlert validationError={changeEmailError} style={{ marginBottom: '0.6em' }} />
          <EmailField intl={intl} username={sessionUser.username} emails={emails} onEdit={editEmail} />
          {ResendEmailComponent(resentValidationEmail ? null : emailSentTo,
            () => { changeEmail(emailSentTo); setResentValidationEmail(true); })}
          {ResendEmailComponent(emailSentTo || sessionUser.emailVerified || resentValidationEmail ? null : sessionUser.username,
            () => { resendVerificationEmail(); setResentValidationEmail(true); })}
          <Divider />
          <ViaValidationAlert validationError={updateSelfError} style={{ marginBottom: '0.6em' }} />
          <DisplayNameField intl={intl} displayName={sessionUser.displayName} onEdit={editDisplayName} />
          <Divider />
          <ViaValidationAlert validationError={updatePasswordError} style={{ marginBottom: '0.6em' }} />
          <PasswordField intl={intl} onEdit={editPassword} />
          <Divider />
          <Row align="top" justify="start" type="flex" style={{ marginBottom: '0.0em' }}>
            <FormattedMessage id="preferredLanguage" />
            :
          </Row>
          <LocaleField justifySelf="right" defaultLocale={sessionUser.locale} onEdit={editLocale} languages={languages} />
        </div>
      </Row>
    </div>
  );
}

MyDetails.propTypes = {
  changePassword: PropTypes.func.isRequired,
  sessionUser: PropTypes.shape({
    username: PropTypes.string.isRequired,
    displayName: PropTypes.string.isRequired,
    locale: PropTypes.string.isRequired,
    linkedRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
    emailVerified: PropTypes.bool.isRequired,
  }).isRequired,
  updateUser: PropTypes.func.isRequired,
  updateSelfError: PropTypes.shape({
    errorMessage: PropTypes.string.isRequired,
    code: PropTypes.number.isRequired,
    errors: PropTypes.arrayOf(PropTypes.string),
  }),
  updatePasswordError: PropTypes.shape({
    errorMessage: PropTypes.string.isRequired,
    code: PropTypes.number.isRequired,
    errors: PropTypes.arrayOf(PropTypes.string),
  }),
  changeEmail: PropTypes.func.isRequired,
  changeEmailError: PropTypes.shape({
    errorMessage: PropTypes.string.isRequired,
    code: PropTypes.number.isRequired,
    errors: PropTypes.arrayOf(PropTypes.string),
  }),
  emailSentTo: PropTypes.string,
  fetchNewEmail: PropTypes.func.isRequired,
  resendVerificationEmail: PropTypes.func.isRequired,
  languages: PropTypes.arrayOf(PropTypes.shape({
    languageCode: PropTypes.string.isRequired,
    languageDescription: PropTypes.string.isRequired,
  })),
  getAvailiableLanguages: PropTypes.func.isRequired,
};

MyDetails.defaultProps = {
  updateSelfError: null,
  updatePasswordError: null,
  changeEmailError: null,
  emailSentTo: null,
  languages: null,
};

/**
 * A function for connecting redux state to the component props.
 */
const mapStateToProps = (state) => {
  const updateSelfRequest = selectUpdateSelfRequest(state);
  const updatePasswordRequest = selectChangePasswordRequest(state);
  const changeEmailRequest = selectSendChangeEmailRequest(state);
  const emailSentToRequest = selectEmailSentToRequest(state);
  const getMostRecentEmailSentTo = () => {
    if (changeEmailRequest && changeEmailRequest.payload) { return changeEmailRequest.payload; }
    if (emailSentToRequest) { return emailSentToRequest.payload; }
    return null;
  };

  return {
    updateSelfError: updateSelfRequest ? updateSelfRequest.error : null,
    updatePasswordError: updatePasswordRequest ? updatePasswordRequest.error : null,
    changeEmailError: changeEmailRequest ? changeEmailRequest.error : null,
    emailSentTo: getMostRecentEmailSentTo(),
    sessionUser: selectSessionUser(state),
    languages: selectAppConfig(state, 'languages') ? selectAppConfig(state, 'languages').languages : null,
  };
};

/**
 * A function for connecting redux action to the component props.
 */
const mapDispatchToProps = dispatch => ({
  changePassword: (currentPassword, newPassword, showNotif) => dispatch(requestChangePassword(currentPassword, newPassword, showNotif)),
  updateUser: user => dispatch(requestUpdateSelf(user)),
  changeEmail: newEmail => dispatch(requestSendChangeEmailEmail(newEmail)),
  fetchNewEmail: () => dispatch(requestEmailSentTo()),
  resendVerificationEmail: () => dispatch(requestSendVerificationEmail()),
  getAvailiableLanguages: () => dispatch(requestSupportedLanguages()),
});

/**
 * Connect the redux state to our component.
 */
export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(MyDetails));
